// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : react hooks for tree component
// =============================================================================
// ---Third-party imports
import { useState, useEffect } from 'react';
// ---Framework imports
import { Sel } from 'uix/registries';
import { size, get, isNil } from 'ldash';
import log from 'uix/logging';
import { useSelector, useActions, useMemo, useApi } from 'uix/hooks';
// ---Local imports
import { getType } from './utils';
// ---Exports
export const useTypeInfo = (otid)=>{
    // blocking loaded
    let aa_node = useSelector((root)=>get(root.data.parts, 'Unbound.ancestorAxisJson.data.ancestorAxis[0]'));
    if (!(String(aa_node.otid) === String(otid))) {
        aa_node = null;
    }
    // async loaded by tree
    let node = useSelector((root)=>root.data.tree.byId[otid]);
    // XXX: take dtypes of node.odid
    let dtypes = useSelector(Sel.types);
    // prefer ancestorAxis node
    if (aa_node) {
        node = aa_node;
    }
    let info = useMemo(()=>{
        if (!node) {
            return null;
        }
        let res = getType(dtypes, node.datatype, node.subtype);
        if (!res) {
            log.warn('unloaded type %o %o', otid, node);
        }
        return {
            cfg: res,
            name: res.name,
            datatype: node.datatype,
            subtype: node.subtype
        };
    }, [
        node,
        dtypes
    ]);
    return info;
};
export const useFullTypeConfig = ()=>{
    /* return full domain types
  merge reduced indexed version with full payload of api/getTypeConfig
  */ let { dname } = useSelector(Sel.request);
    // use version of domain types a cache key
    let tk = useSelector((root)=>root.config.ddata[dname]?.cacheKeys.types);
    // get request local domain types
    let dtypes = useSelector(Sel.types);
    let cacheKeys = tk;
    let { data, error, loading } = useApi('getTypeConfig', {}, {
        cached: true,
        cacheKeys
    });
    if (error) {
        log.exception(error);
    }
    return useMemo(()=>{
        let fullTypes = data || {};
        let typescfg = {
            ...dtypes,
            ...fullTypes
        };
        return {
            typescfg,
            loading
        };
    }, [
        data,
        loading
    ]);
};
export const useDomainTypes = ()=>{
    let dtypes = useSelector(Sel.types);
    return {
        getType: (nameOrDatatype, subtype)=>getType(dtypes, nameOrDatatype, subtype)
    };
};
export const useTypeNames = (data)=>{
    /* given a list of nodes with integers {datatype, subtype}
  add strings: type and datatypeName
  */ let { datatypeMap, byId } = useSelector(Sel.itypes);
    return useMemo(()=>data ? data.map((n)=>({
                ...n,
                type: byId[n.datatype][n.subtype],
                datatypeName: datatypeMap[n.datatype]
            })) : [], [
        data
    ]);
};
